// FAFI Touch T1 (FAFI-Touch-Plan §2.2) — the floating stick, pure math. The DOM shell feeds raw
// client-px pointer coords; this owns base-planting, the normalized vector and the sprint ring.
//
// Conventions match the §13.2 gamepad stick (x right+, y DOWN+; `gamepadMove` reads -y as forward), so
// the already-tested `aimModel.gamepadMove` consumes samples unchanged — the virtual stick IS the stick
// the plan reserved math for.
//
// Sprint is geometric, not a button: pushing past `sprintOn01` of the radius engages BTN.SPRINT (via
// the `Touch:sprint` pseudo-code TouchInput synthesizes) and it holds until the thumb drops below
// `sprintOff01` — hysteresis, so the boundary can never flicker (T-TCH-002). The unclamped distance is
// what the ring compares against: far past the rim is unambiguously a sprint.

export interface StickConfig {
  readonly radiusPx: number;
  readonly sprintOn01: number;
  readonly sprintOff01: number;
}

export const DEFAULT_STICK: StickConfig = { radiusPx: 70, sprintOn01: 0.85, sprintOff01: 0.78 };

export interface StickSample {
  readonly active: boolean;
  /** Stick-space direction, x right+, y down+ (gamepad convention), |(x,y)| ≤ 1. */
  readonly x: number;
  readonly y: number;
  readonly mag: number;
  readonly sprint: boolean;
}

const NEUTRAL: StickSample = { active: false, x: 0, y: 0, mag: 0, sprint: false };

export class TouchStick {
  private baseX = 0;
  private baseY = 0;
  private dx = 0;
  private dy = 0;
  private down_ = false;
  private sprinting = false;

  constructor(private readonly cfg: StickConfig = DEFAULT_STICK) {}

  /** First touch in the zone plants the base where the thumb lands. */
  down(px: number, py: number): void {
    this.down_ = true;
    this.baseX = px;
    this.baseY = py;
    this.dx = 0;
    this.dy = 0;
    this.sprinting = false;
  }

  move(px: number, py: number): void {
    if (!this.down_) return;
    this.dx = px - this.baseX;
    this.dy = py - this.baseY;
    const raw = Math.hypot(this.dx, this.dy) / this.cfg.radiusPx; // unclamped — past the rim still counts
    if (raw >= this.cfg.sprintOn01) this.sprinting = true;
    else if (raw < this.cfg.sprintOff01) this.sprinting = false;
  }

  /** Lift = neutral, instantly — and always ends the sprint. */
  up(): void {
    this.down_ = false;
    this.dx = 0;
    this.dy = 0;
    this.sprinting = false;
  }

  sample(): StickSample {
    if (!this.down_) return NEUTRAL;
    let x = this.dx / this.cfg.radiusPx;
    let y = this.dy / this.cfg.radiusPx;
    const m = Math.hypot(x, y);
    if (m > 1) {
      x /= m;
      y /= m;
    }
    return { active: true, x, y, mag: Math.min(m, 1), sprint: this.sprinting };
  }
}
